/**
 * Shared types and constants for the game client
 */

import Phaser from 'phaser';

// ============ CONSTANTS ============

export const TILE_SIZE = 16;
export const CHUNK_SIZE = 1000;  // 1000px per chunk (doubled from 500)
export const WORLD_CHUNKS = 8;
export const WORLD_WIDTH = CHUNK_SIZE * WORLD_CHUNKS;   // 8000px (doubled from 4000)
export const WORLD_HEIGHT = CHUNK_SIZE * WORLD_CHUNKS;  // 8000px (doubled from 4000)

export const UNCHARTED_WATERS_BUFFER_TILES: number = 64;
export const UNCHARTED_WATERS_BUFFER_PX: number = UNCHARTED_WATERS_BUFFER_TILES * TILE_SIZE;

// ============ COLORS ============

export const COLORS = {
  deepOcean: 0x0a1628,
  ocean: 0x0d2137,
  unchartedDeepOcean: 0x050b14,
  shallowWater: 0x1a4a6e,
  grid: 0x1e3a5f,
  gridLight: 0x2a4a6f,
  oceanOverlay: 0x0a1933, // Darker blue overlay for ocean depth
  sand: 0xf4d03f,
  grass: 0x27ae60,
  mountain: 0x7f8c8d,
  jungle: 0x1e8449,
  desert: 0xe67e22,
  volcanic: 0xc0392b,
  snow: 0xecf0f1,
  playerShip: 0x3498db,
  enemyShip: 0xe74c3c,
  npcShip: 0xf39c12,
  allyShip: 0x2ecc71,
};

// NPC colors based on behavior type (fallback)
export const NPC_BEHAVIOR_COLORS: Record<string, number> = {
  passive: 0x27ae60,      // Green - friendly/neutral
  defensive: 0xf39c12,    // Orange - will fight back
  territorial: 0xe67e22,  // Dark orange - attacks in area
  aggressive: 0xe74c3c,   // Red - attacks on sight
  hunter: 0x9b59b6,       // Purple - actively hunts
  scripted: 0xc0392b,     // Dark red - boss
};

// NPC faction colors - distinct and bold
export const NPC_FACTION_COLORS: Record<string, number> = {
  bandit: 0xd35400,       // Orange/brown - bandits
  pirate: 0x8e44ad,       // Purple - pirates
  navy: 0x2980b9,         // Blue - navy
};

// Biome colors for islands
export const BIOME_COLORS: Record<string, number> = {
  tropical: 0x27ae60,
  desert: 0xe67e22,
  volcanic: 0xc0392b,
  snow: 0xecf0f1,
  jungle: 0x1e8449,
  plains: 0x2ecc71,
};

// ============ INTERFACES ============

// Ship types matching server
export type ShipType = 'raft' | 'sloop' | 'caravel' | 'brigantine' | 'frigate' | 'galleon' | 'man_o_war';

// Ship size multipliers for visual scaling (relative to base TILE_SIZE)
export const SHIP_SIZE_MULTIPLIERS: Record<ShipType, number> = {
  raft: 0.3,      // Tiny - 40% of tile
  sloop: 0.4,     // Small - 60% of tile
  caravel: 0.5,   // Medium - 75% of tile
  brigantine: 0.6,  // Large - 85% of tile
  frigate: 0.7,     // Large warship - 90% of tile
  galleon: 0.8,     // Very large - 95% of tile
  man_o_war: 1.0,   // Massive - full tile
};

export interface ShipData {
  id: string;
  name: string;
  ownerId: string;
  ownerName: string;
  accountUsername?: string;  // Account username for player ships
  accountStatus?: 'user' | 'moderator' | 'admin';
  ownerType: 'player' | 'crew' | 'npc';
  shipType?: ShipType;  // For size scaling
  x: number;
  y: number;
  rotation: number;
  velocityX: number;
  velocityY: number;
  hullHp: number;
  maxHullHp: number;
  sailHp: number;
  maxSailHp: number;
  currentSP: number;
  maxSP: number;
  isSprinting?: boolean;
  crewCount: number;
  combatState: string;
  isOnFire: boolean;
  cannonRange?: number;
  reloadTime?: number;
  // Movement state
  movement?: {
    currentTile: { tileX: number; tileY: number };
    targetTile: { tileX: number; tileY: number } | null;
    path: Array<{ tileX: number; tileY: number }>;
    direction: string;
    movingProgress: number;
    speed: number;  // tiles per tick (server tick-based)
  };
  // NPC behavior
  behaviorType?: 'passive' | 'defensive' | 'territorial' | 'aggressive' | 'hunter';
  isAggroed?: boolean;
  currentTarget?: string | null;
  faction?: 'bandit' | 'pirate' | 'navy';  // navy = legally distinct from Marines
  // Economy
  berries?: number;
  bounty?: number;
  heat?: number;
  // Region security tier
  securityTier?: 'safe_zone' | 'contested' | 'open_waters';
  // Customization
  shipColor?: string;
  // Docking state
  dockedAt?: string;
  dockedPosition?: { x: number; y: number };
  // Fleet info
  fleetId?: string;
  fleetName?: string;
  fleetTag?: string;
  // Expression/title info
  equippedTitle?: string | null;
  titleName?: string | null;
  titleColor?: string | null;
  showFlagAbovePlayer?: boolean;
  flagPattern?: string;
  flagPrimaryColor?: string;
  flagSecondaryColor?: string;
  flagEmblem?: string;
  flagEmblemColor?: string;
  // Progression
  level?: number;
  prestige?: number; // Prestige level (0-10), affects cosmetic appearance options
  // Sanctum instance
  sanctumInstanceId?: string;
  // Cosmetic Customization (for rendering vanity items)
  // NOTE: These fields are populated server-side based on player's equipped cosmetics
  // They allow rendering of purchased/unlocked cosmetic items without additional roundtrips
  shipCustomization?: {
    sails?: string; // Sail design ID
    hull?: string; // Hull coating ID
    flag?: string; // Flag design ID
    aura?: string; // Aura effect ID
    stern?: string; // Stern decoration ID
    ornament?: string; // Hull ornament ID
    crewAppearance?: string; // Crew uniform ID
    lightEffect?: string; // Light effect ID
    // Color customizations
    sailPrimaryColor?: string; // Hex override
    sailAccentColor?: string; // Hex override
    hullPrimaryColor?: string; // Hex override
    hullAccentColor?: string; // Hex override
    auraIntensity?: number; // 0.5-2.0 visual intensity
    glowIntensity?: number; // 0.5-2.0 visual intensity
    achievementBadges?: string[]; // Achievement cosmetic IDs to display
  };
}

export interface NPCData {
  id: string;
  name: string;
  title?: string;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  aiState: string;
  behaviorType: 'passive' | 'defensive' | 'territorial' | 'aggressive' | 'hunter' | 'scripted' | 'ambient_wander' | 'ambient_route' | 'ambient_stationary';
  isAggroed: boolean;
  targetId: string | null;
  aggroRange?: number;  // Combat/aggro range in pixels
  spawnedAt?: number;   // Timestamp when NPC was spawned (for spawn effect)
  gracePeriodEndTime?: number;  // When grace period ends (for visual indicator)
  
  // Boss flag
  isBoss?: boolean;
  
  // Ambient NPC data
  isAmbient?: boolean;
  ambientCategory?: 'civilian' | 'animal' | 'trader' | 'fisherman' | 'sailor';
  tradeRouteId?: string;
  cargoValue?: number;
}

// Trade route data for visible supply lines
export interface TradeRouteData {
  id: string;
  name: string;
  waypoints: { x: number; y: number; name: string }[];
  activeTraders: string[];
  dangerLevel: number;
}

// Ambient NPC colors for rendering (diamond shapes)
export const AMBIENT_NPC_COLORS: Record<string, number> = {
  civilian: 0x88cc88,   // Light green - peaceful
  animal: 0xccaa66,     // Tan/brown - wildlife
  // 'trader' is legacy; fishermen replaces it in ambient systems
  trader: 0x6699cc,
  fisherman: 0x6699cc,  // Blue - water workers
  sailor: 0x99ccff,     // Light blue - seafarers
};

export interface IslandData {
  id: string;
  x: number;
  y: number;
  radius: number;
  biome: string;
  isPermanent: boolean;
  permanentName: string | null;
  shape?: Array<{ x: number; y: number }>;
  // Territory ownership
  ownerId: string | null;
  ownerType: 'player' | 'crew' | 'faction_regime' | 'faction_navy' | 'faction_pirate' | 'faction_merchant' | 'faction_smuggler' | null;
  ownerName: string | null;
  ownerShipColor: string | null;
  customName: string | null;
  claimRadius: number | null;
}

export interface ShipSprite extends Phaser.GameObjects.Container {
  shipId: string;
  shipData: ShipData;
  hull: Phaser.GameObjects.Rectangle;
  hpBar?: Phaser.GameObjects.Graphics;
  nameText?: Phaser.GameObjects.Text;
  aggroIndicator?: Phaser.GameObjects.Rectangle;
  fireEffect?: Phaser.GameObjects.Particles.ParticleEmitter;
}

export interface CombatEvent {
  type: 'cannonball_hit' | 'cannonball_miss' | 'fire_started' | 'fire_extinguished' | 'ship_sinking';
  attackerId: string;
  targetId: string;
  damage?: number;
  position?: { x: number; y: number };
}

export interface BoardingState {
  matchId: string;
  attackerId: string;
  defenderId: string;
  attackerName: string;
  defenderName: string;
  attackerCrew: number;
  defenderCrew: number;
  round: number;
  status: 'waiting' | 'in_progress' | 'finished';
  winner?: string;
  timeRemaining?: number;
}

export interface InventoryItem {
  id: string;
  itemId: string;
  name: string;
  type: string;
  quantity: number;
  rarity: string;
  description?: string;
  effects?: Record<string, number>;
}

export interface ShopItem {
  id: string;
  name: string;
  type: string;
  basePrice: number;
  currentPrice: number;
  stock: number;
  rarity: string;
  description?: string;
}

// ============ FISHING ============

export type FishingSpotType =
  | 'shallow_waters'
  | 'deep_sea'
  | 'coral_reef'
  | 'fishing_hole'
  | 'shipwreck_pool'
  | 'volcanic_waters'
  | 'abyssal_trench'
  | 'whirlpool_edge'
  | 'moonlit_shallows'
  | 'ancient_grounds';

export interface FishingSpotData {
  id: string;
  chunkId: string;
  tileX: number;
  tileY: number;
  type: FishingSpotType;
}

// Colors for fishing spot types
export const FISHING_SPOT_COLORS: Record<FishingSpotType, number> = {
  shallow_waters: 0x5dade2,      // Light blue
  deep_sea: 0x2e86ab,            // Dark blue
  coral_reef: 0xff6b6b,          // Coral pink
  fishing_hole: 0xffd700,        // Gold (rare)
  shipwreck_pool: 0x8b4513,      // Brown (wreck themed)
  volcanic_waters: 0xff7043,     // Lava orange
  abyssal_trench: 0x4b0082,      // Deep purple
  whirlpool_edge: 0x1f6feb,      // Vivid blue
  moonlit_shallows: 0x9b59b6,    // Moonlit violet
  ancient_grounds: 0x2ecc71,     // Mythic green
};

export interface FishCatchData {
  fishId: string;
  name: string;
  category: 'common' | 'uncommon' | 'rare' | 'legendary';
  value: number;
}

// ============ RESOURCE NODES ============

export type ResourceNodeType =
  // Fishing
  | 'shallow_waters' | 'deep_sea' | 'coral_reef' | 'fishing_hole' | 'shipwreck_pool'
  | 'volcanic_waters' | 'abyssal_trench' | 'whirlpool_edge' | 'moonlit_shallows' | 'ancient_grounds'
  // Salvaging
  | 'shipwreck_debris' | 'sunken_rowboat' | 'merchant_wreck' | 'abandoned_cargo' | 'naval_wreck' | 'ancient_ruins'
  // Mining
  | 'stone_quarry' | 'tin_deposit' | 'mineral_outcrop' | 'silver_vein' | 'gold_deposit' | 'coal_seam' | 'volcanic_vent'
  // Foraging
  | 'driftwood_cluster' | 'coconut_palm' | 'seaweed_bed' | 'herb_patch' | 'fruit_grove' | 'mushroom_colony' | 'rare_flower_bed';

export interface ResourceNodeData {
  id: string;
  type: ResourceNodeType;
  name: string;
  x: number;
  y: number;
  tileX: number;
  tileY: number;
  harvestsRemaining: number;
  maxHarvests: number;
  harvestTime: number;
  requiredLevel?: number;
  requiredSkill?: string;
}

export const RESOURCE_NODE_CATEGORIES: Record<ResourceNodeType, 'fishing' | 'salvaging' | 'mining' | 'foraging'> = {
  // Fishing
  shallow_waters: 'fishing',
  deep_sea: 'fishing',
  coral_reef: 'fishing',
  fishing_hole: 'fishing',
  shipwreck_pool: 'fishing',
  volcanic_waters: 'fishing',
  abyssal_trench: 'fishing',
  whirlpool_edge: 'fishing',
  moonlit_shallows: 'fishing',
  ancient_grounds: 'fishing',
  // Salvaging
  shipwreck_debris: 'salvaging',
  sunken_rowboat: 'salvaging',
  merchant_wreck: 'salvaging',
  abandoned_cargo: 'salvaging',
  naval_wreck: 'salvaging',
  ancient_ruins: 'salvaging',
  // Mining
  stone_quarry: 'mining',
  tin_deposit: 'mining',
  mineral_outcrop: 'mining',
  silver_vein: 'mining',
  gold_deposit: 'mining',
  coal_seam: 'mining',
  volcanic_vent: 'mining',
  // Foraging
  driftwood_cluster: 'foraging',
  coconut_palm: 'foraging',
  seaweed_bed: 'foraging',
  herb_patch: 'foraging',
  fruit_grove: 'foraging',
  mushroom_colony: 'foraging',
  rare_flower_bed: 'foraging',
};

// ============ SKILLS ============

export type SkillName = 
  | 'gunnery' | 'tactics'
  | 'fishing' | 'salvaging' | 'foraging' | 'mining'
  | 'shipwright' | 'cooking' | 'smithing' | 'alchemy'
  | 'trading';

export type SkillCategory = 'combat' | 'gathering' | 'production' | 'utility';

export interface SkillData {
  level: number;
  xp: number;
  totalXp: number;
}

export interface PlayerSkills {
  gunnery: SkillData;
  tactics: SkillData;
  fishing: SkillData;
  salvaging: SkillData;
  foraging: SkillData;
  mining: SkillData;
  shipwright: SkillData;
  cooking: SkillData;
  smithing: SkillData;
  alchemy: SkillData;
  trading: SkillData;
}

export interface SkillXPGain {
  skill: SkillName;
  xpGained: number;
  newXp: number;
  newLevel: number;
  leveledUp: boolean;
  totalXp: number;
  infamy: number;
  heat?: number;
  isAggroed: boolean;
}

export interface SkillLevelUp {
  skill: SkillName;
  oldLevel: number;
  newLevel: number;
}

export interface SkillInfo {
  name: SkillName;
  displayName: string;
  category: SkillCategory;
  description: string;
  icon: string;
}

export const SKILL_INFO: Record<SkillName, SkillInfo> = {
  gunnery: { name: 'gunnery', displayName: 'Gunnery', category: 'combat', description: 'Improves cannon accuracy and reload speed.', icon: '[G]' },
  tactics: { name: 'tactics', displayName: 'Tactics', category: 'combat', description: 'Reduces ability cooldowns.', icon: '[T]' },
  fishing: { name: 'fishing', displayName: 'Fishing', category: 'gathering', description: 'Improves catch rate and rare fish chance.', icon: '[F]' },
  salvaging: { name: 'salvaging', displayName: 'Salvaging', category: 'gathering', description: 'Faster salvage and better loot.', icon: '[S]' },
  foraging: { name: 'foraging', displayName: 'Foraging', category: 'gathering', description: 'Better resource yield on islands.', icon: '[R]' },
  mining: { name: 'mining', displayName: 'Mining', category: 'gathering', description: 'Improves ore yield and gem chance.', icon: '[M]' },
  shipwright: { name: 'shipwright', displayName: 'Shipwright', category: 'production', description: 'Better repair efficiency.', icon: '[W]' },
  cooking: { name: 'cooking', displayName: 'Cooking', category: 'production', description: 'Improves food quality and buffs.', icon: '[C]' },
  smithing: { name: 'smithing', displayName: 'Smithing', category: 'production', description: 'Crafts better equipment.', icon: '[I]' },
  alchemy: { name: 'alchemy', displayName: 'Alchemy', category: 'production', description: 'Brews more potent potions.', icon: '[A]' },
  trading: { name: 'trading', displayName: 'Trading', category: 'utility', description: 'Better buy/sell prices.', icon: '[P]' },
};

export const SKILLS_BY_CATEGORY: Record<SkillCategory, SkillName[]> = {
  combat: ['gunnery', 'tactics'],
  gathering: ['fishing', 'salvaging', 'foraging', 'mining'],
  production: ['shipwright', 'cooking', 'smithing', 'alchemy'],
  utility: ['trading'],
};

export const ALL_SKILLS: SkillName[] = [
  'gunnery', 'tactics',
  'fishing', 'salvaging', 'foraging', 'mining',
  'shipwright', 'cooking', 'smithing', 'alchemy',
  'trading',
];

export const CATEGORY_COLORS: Record<SkillCategory, string> = {
  combat: '#e74c3c',
  gathering: '#27ae60',
  production: '#f39c12',
  utility: '#3498db',
};

export const CATEGORY_NAMES: Record<SkillCategory, string> = {
  combat: 'Combat',
  gathering: 'Gathering',
  production: 'Production',
  utility: 'Utility',
};

// XP formula: 10x faster than RuneScape
// XP to next level = floor(level^2 * 10 + level * 5)
export const xpToNextLevel = (level: number): number => {
  if (level >= 100) return Infinity;
  return Math.floor(level * level * 10 + level * 5);
};

export const createDefaultSkillData = (): SkillData => ({
  level: 1,
  xp: 0,
  totalXp: 0,
});

export const createDefaultPlayerSkills = (): PlayerSkills => ({
  gunnery: createDefaultSkillData(),
  tactics: createDefaultSkillData(),
  fishing: createDefaultSkillData(),
  salvaging: createDefaultSkillData(),
  foraging: createDefaultSkillData(),
  mining: createDefaultSkillData(),
  shipwright: createDefaultSkillData(),
  cooking: createDefaultSkillData(),
  smithing: createDefaultSkillData(),
  alchemy: createDefaultSkillData(),
  trading: createDefaultSkillData(),
});

// ============ HOTBAR TYPES ============

export interface HotbarSlotItem {
  itemId: string;
  quantity: number;  // For consumables; 1 for gear
  type: 'consumable' | 'gear' | 'wildcard';
}

export interface HotbarSlot {
  slotIndex: number;  // 0-5 (0 is wildcard power, 1-5 are consumables)
  item: HotbarSlotItem | null;
  cooldownUntil: number;  // Timestamp when cooldown expires (0 = no cooldown)
}

export interface HotbarConfig {
  slots: HotbarSlot[];
  lastUpdated: number;
}

export const createDefaultHotbarConfig = (): HotbarConfig => ({
  slots: Array.from({ length: 6 }, (_, i) => ({
    slotIndex: i,
    item: null,
    cooldownUntil: 0,
  })),
  lastUpdated: Date.now(),
});
